import { useTranslation } from "react-i18next";
import { IChartPlotProps } from "#src/@types/chart";
import { EndpointOverviewGraphWrapper } from "./wrappers/GraphWrapper";
import { EndpointOverviewStatWrapper } from "./wrappers/StatWrapper";
import { IEndpointOverviewResponse } from "#src/services/endpoint/types";
import { TAILWIND_COLORS } from "#src/utils/common";
import { pathname } from "#src/config/pathname";
import { OsEnum } from "../utils";

export const EndpointOverviewStatsAndGraphs = ({
  workspaceId,
  data,
}: {
  workspaceId: string;
  data: IEndpointOverviewResponse["count"];
}) => {
  const { t } = useTranslation("endpoint", { keyPrefix: "overview" });

  // --------------- COMPUTED ---------------

  const graphColorByOs: { [T in OsEnum | "others"]: string } = {
    [OsEnum.WINDOWS]: TAILWIND_COLORS["blue-dark-v2-400"],
    [OsEnum.APPLE]: TAILWIND_COLORS["gray-v2-400"],
    [OsEnum.UBUNTU]: TAILWIND_COLORS["orange-v2-400"],
    [OsEnum.ANDROID]: TAILWIND_COLORS["success-v2-400"],
    others: TAILWIND_COLORS["warning-v2-300"],
  };

  const devicesByStatusChartProps: IChartPlotProps[] = [
    {
      id: "managed",
      name: t("graphs.devicesByStatus.managed"),
      fill: TAILWIND_COLORS["brand-v2-400"],
      value: data.manage.find((item) => item.managed === "managed")?.count ?? 0,
    },
    {
      id: "unmanaged",
      name: t("graphs.devicesByStatus.unmanaged"),
      fill: TAILWIND_COLORS["gray-v2-400"],
      value:
        data.manage.find((item) => item.managed === "unmanaged")?.count ?? 0,
    },
  ];

  const devicesByOsChartProps: IChartPlotProps[] = [
    {
      id: "windows",
      name: t("graphs.byOs.windows"),
      fill: graphColorByOs[OsEnum.WINDOWS],
      value: data.os.find((item) => item.os === OsEnum.WINDOWS)?.count ?? 0,
    },
    {
      id: "apple",
      name: t("graphs.byOs.apple"),
      fill: graphColorByOs[OsEnum.APPLE],
      value: data.os.find((item) => item.os === OsEnum.APPLE)?.count ?? 0,
    },
    {
      id: "ubuntu",
      name: t("graphs.byOs.ubuntu"),
      fill: graphColorByOs[OsEnum.UBUNTU],
      value: data.os.find((item) => item.os === OsEnum.UBUNTU)?.count ?? 0,
    },
    {
      id: "android",
      name: t("graphs.byOs.android"),
      fill: graphColorByOs[OsEnum.ANDROID],
      value: data.os.find((item) => item.os === OsEnum.ANDROID)?.count ?? 0,
    },
    {
      id: "others",
      name: t("graphs.byOs.others"),
      fill: graphColorByOs["others"],
      value: data.os
        .filter(
          (item) =>
            ![OsEnum.WINDOWS, OsEnum.APPLE, OsEnum.UBUNTU, OsEnum.ANDROID]
              .map((item) => item.toString())
              .includes(item.os)
        )
        .reduce((prev, cur) => prev + cur.count, 0),
    },
  ];

  const failedPoliciesByOsChartProps: IChartPlotProps[] = [
    {
      id: "windows",
      name: t("graphs.byOs.windows"),
      fill: graphColorByOs[OsEnum.WINDOWS],
      value:
        data.failed_policies.find((item) => item.os === OsEnum.WINDOWS)
          ?.count ?? 0,
    },
    {
      id: "apple",
      name: t("graphs.byOs.apple"),
      fill: graphColorByOs[OsEnum.APPLE],
      value:
        data.failed_policies.find((item) => item.os === OsEnum.APPLE)?.count ??
        0,
    },
    {
      id: "ubuntu",
      name: t("graphs.byOs.ubuntu"),
      fill: graphColorByOs[OsEnum.UBUNTU],
      value:
        data.failed_policies.find((item) => item.os === OsEnum.UBUNTU)?.count ??
        0,
    },
    {
      id: "android",
      name: t("graphs.byOs.android"),
      fill: graphColorByOs[OsEnum.ANDROID],
      value:
        data.failed_policies.find((item) => item.os === OsEnum.ANDROID)
          ?.count ?? 0,
    },
    {
      id: "others",
      name: t("graphs.byOs.others"),
      fill: graphColorByOs["others"],
      value: data.failed_policies
        .filter(
          (item) =>
            ![OsEnum.WINDOWS, OsEnum.APPLE, OsEnum.UBUNTU, OsEnum.ANDROID]
              .map((item) => item.toString())
              .includes(item.os)
        )
        .reduce((prev, cur) => prev + cur.count, 0),
    },
  ];

  const appsWithVulnsByOsChartProps: IChartPlotProps[] = [
    {
      id: "windows",
      name: t("graphs.byOs.windows"),
      fill: graphColorByOs[OsEnum.WINDOWS],
      value:
        data.vuln_programs.find((item) => item.os === OsEnum.WINDOWS)?.count ??
        0,
    },
    {
      id: "apple",
      name: t("graphs.byOs.apple"),
      fill: graphColorByOs[OsEnum.APPLE],
      value:
        data.vuln_programs.find((item) => item.os === OsEnum.APPLE)?.count ?? 0,
    },
    {
      id: "ubuntu",
      name: t("graphs.byOs.ubuntu"),
      fill: graphColorByOs[OsEnum.UBUNTU],
      value:
        data.vuln_programs.find((item) => item.os === OsEnum.UBUNTU)?.count ??
        0,
    },
    {
      id: "android",
      name: t("graphs.byOs.android"),
      fill: graphColorByOs[OsEnum.ANDROID],
      value:
        data.vuln_programs.find((item) => item.os === OsEnum.ANDROID)?.count ??
        0,
    },
    {
      id: "others",
      name: t("graphs.byOs.others"),
      fill: graphColorByOs["others"],
      value: data.vuln_programs
        .filter(
          (item) =>
            ![OsEnum.WINDOWS, OsEnum.APPLE, OsEnum.UBUNTU, OsEnum.ANDROID]
              .map((item) => item.toString())
              .includes(item.os)
        )
        .reduce((prev, cur) => prev + cur.count, 0),
    },
  ];

  // --------------- RENDER ---------------

  return (
    <div className="px-8 grid grid-cols-4 gap-4">
      <EndpointOverviewStatWrapper
        workspaceId={workspaceId}
        title={t("statistics.devices")}
        amount={data.devices}
        path={pathname.ENDPOINT_DEVICES}
      />
      <EndpointOverviewStatWrapper
        workspaceId={workspaceId}
        title={t("statistics.software")}
        amount={data.applications}
      />
      <EndpointOverviewStatWrapper
        workspaceId={workspaceId}
        title={t("statistics.departments")}
        amount={data.departments}
        path={pathname.ENDPOINT_DIRECTORY_DEPARTMENTS}
      />
      <EndpointOverviewStatWrapper
        workspaceId={workspaceId}
        title={t("statistics.users")}
        amount={data.users}
        path={pathname.ENDPOINT_DIRECTORY_USERS}
      />
      <EndpointOverviewGraphWrapper
        title={t("graphs.devicesByStatus.title")}
        chartData={devicesByStatusChartProps}
        getUnit={(count: number) =>
          t("graphs.devicesByStatus.device", { count })
        }
      />
      <EndpointOverviewGraphWrapper
        title={t("graphs.devicesByOs.title")}
        chartData={devicesByOsChartProps}
        getUnit={(count: number) => t("graphs.devicesByOs.device", { count })}
      />
      <EndpointOverviewGraphWrapper
        title={t("graphs.failedPoliciesByOs.title")}
        chartData={failedPoliciesByOsChartProps}
        getUnit={(count: number) =>
          t("graphs.failedPoliciesByOs.policy", { count })
        }
      />
      <EndpointOverviewGraphWrapper
        title={t("graphs.appsWithVulnsByOs.title")}
        chartData={appsWithVulnsByOsChartProps}
        getUnit={(count: number) =>
          t("graphs.appsWithVulnsByOs.app", { count })
        }
      />
    </div>
  );
};
